// Small presentational helpers shared across the Dashboard zones (DASH.B1).
// Kept pure + dependency-free so they're trivially unit-testable.

import type { RagStatus } from '../types/dashboard';

const PHASE_LABELS: Record<string, string> = {
  phase_0_bootstrap: 'Bootstrap',
  phase_1_requirements: 'Requirements',
  phase_2_architecture: 'Architecture',
  phase_2_5_ux_design: 'UX Design',
  phase_3_sprint_planning: 'Sprint Planning',
  phase_4_development: 'Development',
  phase_5_code_review: 'Code Review',
  phase_6_testing: 'Testing',
  phase_7_deployment: 'Deployment',
  phase_8_retrospective: 'Retrospective',
};

export function phaseLabel(phaseKey: string | null | undefined): string {
  if (!phaseKey) return '—';
  return PHASE_LABELS[phaseKey] ?? phaseKey;
}

/** Coarse relative-time string. `now` is injectable for deterministic tests. */
export function formatTimeAgo(isoDate: string, now: number = Date.now()): string {
  const ts = new Date(isoDate).getTime();
  if (Number.isNaN(ts)) return '—';
  const diff = now - ts;
  if (diff < 0) return 'Just now';
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * RAG by urgency for a set of due-dated items (e.g. pending approvals).
 * Overdue → RED; due within 48h → AMBER; otherwise GREEN. Empty → GREEN.
 * `now` is injectable for deterministic tests.
 */
export function ragForDueDates(
  items: Array<{ dueAt: string }>,
  now: number = Date.now(),
): RagStatus {
  if (items.length === 0) return 'GREEN';
  const AMBER_WINDOW_MS = 48 * 60 * 60 * 1000;
  let rag: RagStatus = 'GREEN';
  for (const item of items) {
    const due = new Date(item.dueAt).getTime();
    if (Number.isNaN(due)) continue;
    if (due < now) return 'RED';
    if (due - now <= AMBER_WINDOW_MS) rag = 'AMBER';
  }
  return rag;
}
